import * as React from 'react';

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './components/ui/sheet.js';
import { DialogContentContainerContext } from './DimeDialog.js';
import { isEscapeForComboboxPopup } from './lib/is-escape-for-combobox-popup.js';
import { cn } from './lib/utils.js';

const SHEET_SIZE_CLASSES = {
  default: '',
  wide: 'data-[side=left]:w-full data-[side=right]:w-full data-[side=left]:sm:max-w-[min(1200px,85vw)] data-[side=right]:sm:max-w-[min(1200px,85vw)]',
} as const;

export function DimeSheet({ ...props }: Readonly<React.ComponentProps<typeof Sheet>>) {
  return <Sheet {...props} />;
}

export function DimeSheetTrigger({
  ...props
}: Readonly<React.ComponentProps<typeof SheetTrigger>>) {
  return <SheetTrigger {...props} />;
}

export function DimeSheetClose({ ...props }: Readonly<React.ComponentProps<typeof SheetClose>>) {
  return <SheetClose {...props} />;
}

// Like DimeDialogContent: Radix's scroll lock exempts only this node, so a Combobox popup must portal into it to stay scrollable.
/** `wide` lets a side sheet take most of the screen; its classes share the base cap's `data-[side]` variants so they replace it. */
export function DimeSheetContent({
  children,
  ref,
  onEscapeKeyDown,
  size = 'default',
  className,
  ...props
}: Readonly<
  React.ComponentProps<typeof SheetContent> & { size?: keyof typeof SHEET_SIZE_CLASSES }
>) {
  const [contentEl, setContentEl] = React.useState<HTMLDivElement | null>(null);
  const mergedRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      setContentEl(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );
  return (
    <SheetContent
      {...props}
      className={cn(SHEET_SIZE_CLASSES[size], className)}
      ref={mergedRef}
      onEscapeKeyDown={(event) => {
        // Radix sees Escape first, so without this an open combobox list, or a control in its popup, would close the whole sheet.
        if (isEscapeForComboboxPopup(event.target)) {
          event.preventDefault();
          return;
        }
        onEscapeKeyDown?.(event);
      }}
    >
      <DialogContentContainerContext.Provider value={contentEl}>
        {children}
      </DialogContentContainerContext.Provider>
    </SheetContent>
  );
}

export function DimeSheetHeader({ ...props }: Readonly<React.ComponentProps<typeof SheetHeader>>) {
  return <SheetHeader {...props} />;
}

export function DimeSheetFooter({ ...props }: Readonly<React.ComponentProps<typeof SheetFooter>>) {
  return <SheetFooter {...props} />;
}

export function DimeSheetTitle({ ...props }: Readonly<React.ComponentProps<typeof SheetTitle>>) {
  return <SheetTitle {...props} />;
}

export function DimeSheetDescription({
  ...props
}: Readonly<React.ComponentProps<typeof SheetDescription>>) {
  return <SheetDescription {...props} />;
}
